import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "../../lib/smooth";
import { useLanguage } from "../../i18n/LanguageContext";

const SPY_IDS = ["platform", "roles", "institutions", "government", "about", "contact"];

export const Header = () => {
  const { t } = useLanguage();
  const NAV = [...t.nav.items, { label: t.nav.contact, href: "#contact" }];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-40% 0px -55% 0px" }
    );
    SPY_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-white/95 shadow-[0_1px_0_rgba(4,32,63,0.08)] backdrop-blur-md" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <button
            data-testid="logo-home-button"
            onClick={() => go("#top")}
            className="flex items-center gap-3"
          >
            <img
              src={scrolled ? "/logo-dark-wm.png" : "/logo-white-wm.png"}
              alt="CYRA"
              className="h-8 w-auto"
            />
            <span className={`h-5 w-px ${scrolled ? "bg-kn-navy/15" : "bg-white/25"}`} />
            <span className={`font-serif text-lg italic leading-none ${scrolled ? "text-kn-blue" : "text-kn-cyan"}`}>
              Krida
            </span>
          </button>

          {/* capsule navigation — separate floating element */}
          <nav
            data-testid="desktop-nav"
            className={`absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 rounded-full border px-2 py-1.5 backdrop-blur-md transition-all duration-500 lg:flex ${
              scrolled
                ? "border-kn-navy/10 bg-white/90 shadow-[0_12px_40px_-16px_rgba(4,32,63,0.35)]"
                : "border-white/10 bg-white/[0.06]"
            }`}
          >
            {NAV.map((n) => {
              const id = n.href.slice(1);
              const isActive = active === id;
              return (
                <button
                  key={n.href}
                  data-testid={`nav-${id}`}
                  onClick={() => go(n.href)}
                  className={`rounded-full px-4 py-1.5 text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive
                      ? scrolled
                        ? "bg-kn-navy text-white"
                        : "bg-white/15 text-white"
                      : scrolled
                        ? "text-kn-navy/60 hover:bg-kn-navy/5 hover:text-kn-navy"
                        : "text-white/60 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {n.label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="header-join-link"
              onClick={() => go("#join")}
              className={`group hidden items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 lg:inline-flex ${
                scrolled ? "text-kn-blue hover:text-kn-navy" : "text-kn-cyan hover:text-white"
              }`}
            >
              Join Krida
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className={`lg:hidden ${scrolled ? "text-kn-navy" : "text-white"}`}
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-kn-navy"
            data-testid="mobile-menu"
          >
            <div className="flex h-[72px] items-center justify-between px-6">
              <span className="flex items-center gap-3">
                <img src="/logo-white-wm.png" alt="CYRA" className="h-7 w-auto" />
                <span className="h-5 w-px bg-white/25" />
                <span className="font-serif text-lg italic leading-none text-kn-cyan">Krida</span>
              </span>
              <div className="flex items-center gap-4">
                <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu">
                  <X className="h-6 w-6 text-white" strokeWidth={1.5} />
                </button>
              </div>
            </div>
            <nav className="flex flex-col gap-2 px-6 pt-10">
              {NAV.map((n, i) => (
                <motion.button
                  key={n.href}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`mobile-nav-${n.href.slice(1)}`}
                  onClick={() => go(n.href)}
                  className="border-b border-white/10 py-5 text-left text-2xl font-light text-white"
                >
                  {n.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + NAV.length * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                data-testid="mobile-nav-join"
                onClick={() => go("#join")}
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-brand-gradient py-4 text-sm font-semibold text-white"
              >
                Join Krida
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
